import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { leverageFundAPI } from '@/lib/api';
import { Wallet, TrendingUp, BarChart3, Lock } from 'lucide-react';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const formatINR = (v) => v == null ? '₹0.00' : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v);

export default function LeverageFundPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    leverageFundAPI.getStatus().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout><div className="p-8 text-center text-sm text-[var(--text-secondary)]">Loading...</div></DashboardLayout>;

  const d = data || {};
  const progress = (d.leverage_balance || d.leverage_fund) > 0 ? Math.min(((d.leverage_used || 0) / (d.leverage_balance || d.leverage_fund)) * 100, 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="leverage-fund-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-blue-500/15 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Leverage Fund</h1>
            <p className="text-xs text-[var(--text-secondary)]">Extra trading funds provided by the platform</p>
          </div>
        </div>

        {/* Leverage Fund Amount Card */}
        <div className="p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg" data-testid="leverage-main-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Leverage Fund</h2>
            {d.active ? (
              <span className="px-3 py-1 bg-blue-500/15 text-blue-400 rounded text-xs font-semibold">ACTIVE</span>
            ) : (
              <span className="px-3 py-1 bg-zinc-500/15 text-[var(--text-tertiary)] rounded text-xs">INACTIVE</span>
            )}
          </div>
          <p className="text-3xl font-bold font-mono text-blue-400" data-testid="leverage-amount">{formatCurrency(d.leverage_balance || d.leverage_fund)}</p>
          {d.active && (
            <>
              <div className="mt-2 flex items-center gap-3">
                <span className="text-xs text-emerald-400 font-semibold">LEVERAGE BALANCE</span>
                <span className="text-xs text-emerald-400 font-mono">({formatCurrency(d.leverage_balance || d.leverage_fund)})</span>
              </div>
              {d.leverage_used > 0 && (
                <div className="mt-1 flex items-center gap-3">
                  <span className="text-xs text-amber-400 font-semibold">USED IN TRADES</span>
                  <span className="text-xs text-amber-400 font-mono">({formatCurrency(d.leverage_used)})</span>
                </div>
              )}
              {d.remaining_to_use > 0 && (
                <div className="mt-1 flex items-center gap-3">
                  <span className="text-xs text-blue-400 font-semibold">REMAINING TO USE</span>
                  <span className="text-xs text-blue-400 font-mono">({formatCurrency(d.remaining_to_use)})</span>
                </div>
              )}
              {d.outstanding > 0 && (
                <div className="mt-1 flex items-center gap-3">
                  <span className="text-xs text-red-400 font-semibold">OUTSTANDING REPAYMENT</span>
                  <span className="text-xs text-red-400 font-mono">({formatCurrency(d.outstanding)})</span>
                  <span className="text-xs text-red-400/70 font-mono">({formatINR(d.outstanding_inr)})</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="card-wallet-balance">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Wallet Balance</span>
            </div>
            <p className="text-2xl font-bold font-mono text-emerald-400">{formatCurrency(d.wallet_balance)}</p>
          </div>
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="card-leverage-fund">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Leverage Balance</span>
            </div>
            <p className="text-2xl font-bold font-mono text-blue-400">{formatCurrency(d.leverage_balance || d.leverage_fund)}</p>
          </div>
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="card-total-balance">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-[var(--text-primary)]" />
              <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">Total Trading Balance</span>
            </div>
            <p className="text-2xl font-bold font-mono text-[var(--text-primary)]">{formatCurrency(d.total_trading_balance)}</p>
          </div>
        </div>

        {d.active && (
          <div className="p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg" data-testid="volume-progress">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">Leverage Utilization</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">Leverage Balance</span>
                <span className="font-mono text-[var(--text-primary)]">{formatCurrency(d.leverage_balance || d.leverage_fund)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">Utilized</span>
                <span className="font-mono text-emerald-400">{progress.toFixed(1)}%</span>
              </div>
              <div className="w-full h-3 bg-[var(--bg-primary)] rounded-full overflow-hidden border border-[var(--border-subtle)]">
                <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">{formatCurrency(d.leverage_used || 0)} used of {formatCurrency(d.leverage_balance || d.leverage_fund)}</span>
                <span className="text-[var(--text-secondary)]">Remaining: {formatCurrency(d.remaining_to_use || 0)}</span>
              </div>
            </div>
          </div>
        )}

        {!d.active && (
          <div className="p-8 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg text-center" data-testid="no-leverage">
            <Lock className="w-8 h-8 text-[var(--text-tertiary)] mx-auto mb-3" />
            <p className="text-sm text-[var(--text-secondary)]">No leverage fund is currently assigned to your account.</p>
            <p className="text-xs text-[var(--text-tertiary)] mt-1">Contact support if you need additional trading funds.</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
